import React from 'react';

import PropTypes from 'prop-types';
import { Button, Segment, Table, Container } from 'semantic-ui-react';

import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _merge from 'lodash/merge';
import _set from 'lodash/set';

import { multiCaseGet } from 'libs/format';

import { SETTING_LOGICAL_ORDER, SETTING_TYPES } from './AccountSettings.constants';
import { GoogleLocationPropType, LocationSettingsPropType } from './AccountSettings.types';
import { getLocationSyncSettings } from './AccountSettings.utils';
import PullSettings from './components/PullSettings/PullSettings';
import PushSettings from './components/PushSettings/PushSettings';

import './AccountSettings.scss';

const propTypes = {
  loading: PropTypes.bool,
  associatedLocations: PropTypes.arrayOf(GoogleLocationPropType),
  locationSettings: LocationSettingsPropType,
  onDisconnect: PropTypes.func.isRequired,
  onLocationSettingsChange: PropTypes.func.isRequired,
  excludedSettings: PropTypes.arrayOf(PropTypes.string),
};

const defaultProps = {
  loading: false,
  associatedLocations: [],
  locationSettings: {},
  excludedSettings: [],
};

function AccountSettings({
  loading,
  onDisconnect,
  associatedLocations,
  locationSettings,
  onLocationSettingsChange,
  excludedSettings,
}) {
  function getSyncSettings(location) {
    return getLocationSyncSettings(locationSettings, location);
  }

  function onSettingsChange(location, settingName, settingType, newPartialSettingsForType) {
    const fullSyncSettings = _cloneDeep(getLocationSyncSettings(locationSettings, location));
    const newSyncSettingsForType = _merge(
      multiCaseGet(fullSyncSettings, `${settingName}.${settingType}.settings`),
      newPartialSettingsForType,
    );

    const isPull = settingType === SETTING_TYPES.PULL;
    const isPush = settingType === SETTING_TYPES.PUSH;

    const otherSettingType = isPull ? SETTING_TYPES.PUSH : SETTING_TYPES.PULL;
    const otherSupported = multiCaseGet(
      fullSyncSettings,
      `${settingName}.${otherSettingType}.supported`,
    );

    const settingForTypeEnabled = _get(newSyncSettingsForType, 'enabled');
    if (isPull) {
      // If pull is disabled, push is disabled too
      if (!settingForTypeEnabled && otherSupported) {
        _set(
          fullSyncSettings,
          `${settingName}.${otherSettingType}.settings.enabled`,
          settingForTypeEnabled,
        );
      }
    } else if (isPush) {
      // If push is enabled, pull is enabled too
      if (settingForTypeEnabled && otherSupported) {
        _set(
          fullSyncSettings,
          `${settingName}.${otherSettingType}.settings.enabled`,
          settingForTypeEnabled,
        );
      }
    }

    _set(fullSyncSettings, `${settingName}.${settingType}.settings`, newSyncSettingsForType);
    onLocationSettingsChange(location, fullSyncSettings);
  }

  function onPullSettingChange(location, settingName, newPartialSettings) {
    onSettingsChange(location, settingName, SETTING_TYPES.PULL, newPartialSettings);
  }

  function onPushSettingChange(location, settingName, newPartialSettings) {
    onSettingsChange(location, settingName, SETTING_TYPES.PUSH, newPartialSettings);
  }

  return (
    <Segment attached="bottom" loading={loading} className="account-settings">
      {associatedLocations !== null &&
        associatedLocations.map((location) => {
          const locationId = multiCaseGet(location, 'locationId');
          const locationTitle = multiCaseGet(location, 'title');
          const storefrontAddress = multiCaseGet(location, 'storefrontAddress');
          const addressLine = multiCaseGet(storefrontAddress, 'addressLines.0');
          const locality = multiCaseGet(storefrontAddress, 'locality');

          const settings = getSyncSettings(location);
          let names = Object.keys(settings).sort(
            (settingA, settingB) =>
              SETTING_LOGICAL_ORDER.indexOf(settingA) - SETTING_LOGICAL_ORDER.indexOf(settingB),
          );

          if (!_isEmpty(excludedSettings)) {
            names = names.filter((name) => !excludedSettings.includes(name));
          }

          return (
            <Table key={locationId}>
              <Table.Header>
                <Table.HeaderCell>{`Location Settings - ${locationTitle}, ${addressLine}, ${locality}`}</Table.HeaderCell>
                <Table.HeaderCell textAlign="right">Enabled</Table.HeaderCell>
              </Table.Header>
              <Table.Body>
                {names.map((name) => {
                  const setting = settings[name];

                  return (
                    <React.Fragment key={name}>
                      <PullSettings
                        setting={setting}
                        name={name}
                        location={location}
                        onSettingChange={(newSettings) =>
                          onPullSettingChange(location, name, newSettings)
                        }
                      />
                      <PushSettings
                        setting={setting}
                        name={name}
                        location={location}
                        onSettingChange={(newSettings) =>
                          onPushSettingChange(location, name, newSettings)
                        }
                      />
                    </React.Fragment>
                  );
                })}
              </Table.Body>
            </Table>
          );
        })}
      <Container textAlign="right">
        <Button negative onClick={onDisconnect} content="Disconnect" />
      </Container>
    </Segment>
  );
}

AccountSettings.propTypes = propTypes;
AccountSettings.defaultProps = defaultProps;

export default AccountSettings;
